import React, { useCallback, useMemo, useState } from 'react';
import { SelectOption, Modal } from 'ui-components/src';
import {
  ActionButton,
  Actions,
  Content,
  Copy,
  Field,
  FolderSelect,
  Hint,
  PathInput,
} from './styles';

export const MoveAttachmentModal = ({
  active,
  label,
  existingFolders,
  initialPath,
  onClose,
  onSubmit,
}: MoveAttachmentModalProps) => {
  const initialSelectedFolder = findInitialFolder(existingFolders, initialPath);
  const [selectedFolderPath, setSelectedFolderPath] = useState(
    initialSelectedFolder,
  );
  const [subfolderPath, setSubfolderPath] = useState(
    getSubfolderPath(initialPath, initialSelectedFolder),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const folderOptions = useMemo<SelectOption[]>(
    () => [
      { label: 'Default', value: '' },
      ...existingFolders.map((folderPath) => ({
        label: folderPath,
        value: folderPath,
      })),
    ],
    [existingFolders],
  );

  const selectedFolder = useMemo(
    () =>
      folderOptions.find((option) => option.value === selectedFolderPath) ||
      folderOptions[0],
    [folderOptions, selectedFolderPath],
  );

  const targetPath = useMemo(
    () => joinFolderPath(selectedFolderPath, subfolderPath),
    [selectedFolderPath, subfolderPath],
  );

  const close = useCallback(() => {
    const nextSelectedFolder = findInitialFolder(existingFolders, initialPath);
    setSelectedFolderPath(nextSelectedFolder);
    setSubfolderPath(getSubfolderPath(initialPath, nextSelectedFolder));
    setIsSubmitting(false);
    onClose();
  }, [existingFolders, initialPath, onClose]);

  const submit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(targetPath || null);
      close();
    } finally {
      setIsSubmitting(false);
    }
  }, [close, onSubmit, targetPath]);

  return (
    <Modal active={active} title="Move document" onClose={close}>
      <Content>
        <Copy>
          Move <strong>{label}</strong> to an existing folder or enter a new
          folder path.
        </Copy>
        <Field>
          <FolderSelect
            options={folderOptions}
            selected={selectedFolder}
            placeholder="Select a existing folder"
            onSelect={(option) => {
              setSelectedFolderPath(option?.value || '');
              setSubfolderPath('');
            }}
          />
        </Field>
        <Field>
          <PathInput
            label="Subfolder(s) path"
            value={subfolderPath}
            onChange={(event) => setSubfolderPath(event.target.value)}
            onKeyDown={async (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                await submit();
              }
            }}
          />
          <Hint>
            Optionally add subfolder(s) here, for example{' '}
            <strong>Agent/CERS</strong>. <br />
            Leave this empty to move the document directly into the selected
            folder.
          </Hint>
        </Field>
        <Actions>
          <ActionButton secondary onClick={close}>
            Cancel
          </ActionButton>
          <ActionButton loading={isSubmitting} onClick={submit}>
            Move
          </ActionButton>
        </Actions>
      </Content>
    </Modal>
  );
};

export interface MoveAttachmentModalProps {
  active: boolean;
  label: string;
  existingFolders: string[];
  initialPath: string;
  onClose: () => void;
  onSubmit: (path: string | null) => Promise<void>;
}

const findInitialFolder = (existingFolders: string[], initialPath: string) =>
  existingFolders.find((folderPath) => folderPath === initialPath) || '';

const getSubfolderPath = (fullPath: string, basePath: string) => {
  const normalizedFullPath = normalizePath(fullPath);
  const normalizedBasePath = normalizePath(basePath);

  if (!normalizedBasePath) return normalizedFullPath;
  if (!normalizedFullPath.startsWith(normalizedBasePath)) return '';

  return normalizedFullPath
    .slice(normalizedBasePath.length)
    .replace(/^\/+/, '');
};

const joinFolderPath = (basePath: string, subfolderPath: string) => {
  const normalizedBasePath = normalizePath(basePath);
  const normalizedSubfolderPath = normalizePath(subfolderPath);

  if (!normalizedBasePath) return normalizedSubfolderPath;
  if (!normalizedSubfolderPath) return normalizedBasePath;

  return `${normalizedBasePath}/${normalizedSubfolderPath}`;
};

const normalizePath = (value: string) => value.trim().replace(/^\/+|\/+$/g, '');
